import { camelizeKeys } from 'humps';
import { ApiError } from '@rambler-id/errors';
import { getFirstPartyOrigin } from '@rambler-id/itp';
import { importSecret, signPayload } from '../utils/secret';
import { decamelizeRpcKeys } from '../utils/keys';
import { createRequestId as createDefaultRequestId } from '../utils/id';
import { encryptData } from '../utils/encrypt';
import { getHeaders, RAMBLER_RPC_ID, CLIENT_REQUEST_ID } from '../utils/headers';
import { GET_SERVER_TIME } from '../utils/constants';
import { request } from './transport';
export const rpcSecret = importSecret();
export const apiV3 = async (method, payload = {}, options = {}) => {
    const { apiOrigin = getFirstPartyOrigin(), encryptionFields = [], encryptionKey, trackRequest, createRequestId = createDefaultRequestId } = options;
    const url = `${apiOrigin}/api/v3${method}`;
    const params = { ...payload };
    if (encryptionKey) {
        const encryptedData = await encryptData(params, encryptionKey, encryptionFields, () => apiV3(`/legacy/${GET_SERVER_TIME}`, undefined, options));
        Object.assign(params, encryptedData);
        if (encryptedData.encrypted) {
            encryptionFields.forEach((field) => delete params[field]);
        }
    }
    const requestId = createRequestId();
    const shouldSignRequest = !!rpcSecret;
    const body = JSON.stringify({
        ...(requestId && {
            id: requestId
        }),
        params: decamelizeRpcKeys({
            ...params,
            ...(shouldSignRequest && {
                __skipCaptcha: 1,
                __skipRateLimit: 1
            })
        })
    });
    let signature;
    if (shouldSignRequest && !!rpcSecret) {
        signature = await signPayload(rpcSecret.kid, rpcSecret.keyString, body);
    }
    const headers = getHeaders({ ...options.headers, requestId, signature });
    let json;
    try {
        const response = await request(url, {
            body,
            method: 'post',
            credentials: 'include',
            headers
        });
        json = await response.json();
        trackRequest?.({
            localTime: Math.floor(Date.now() / 1000),
            location: window.location.href,
            endpoint: url,
            httpMethod: 'post',
            httpStatus: response.status,
            request: {
                requestId: requestId ?? null,
                body: {
                    method,
                    params: decamelizeRpcKeys(payload)
                }
            },
            response: {
                responseId: response.headers.get(RAMBLER_RPC_ID) ?? null,
                body: json
            }
        });
    }
    catch (error) {
        throw new ApiError({
            code: 0,
            message: 'api does not respond',
            details: {
                method,
                requestId: headers[CLIENT_REQUEST_ID],
                ...headers
            }
        });
    }
    const { status, ...rest } = camelizeKeys(json.result || {});
    if (status === 'OK') {
        return rest;
    }
    const { error, ...errorInfo } = camelizeKeys(json?.error?.extra?.__body_error || {});
    if (error == null) {
        throw new ApiError({
            code: 0,
            message: 'api failed',
            details: {
                method,
                requestId: headers[CLIENT_REQUEST_ID],
                ...headers
            }
        });
    }
    throw new ApiError({
        code: error.errno,
        message: error.strerror,
        details: {
            method,
            requestId: headers[CLIENT_REQUEST_ID],
            ...headers,
            ...(error.currentThreadExtra && {
                currentThreadExtra: error.currentThreadExtra
            }),
            ...errorInfo,
            ...rest
        }
    });
};
export const createApiV3 = (rpcOptions = {}) => (method, payload = {}, requestOptions = {}) => {
    const options = { ...rpcOptions, ...requestOptions };
    return apiV3(method, payload, options);
};
